import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import {
  PasDateTimeComponent,
  PasFormValidationMessage,
  PasInputFieldComponent,
  PasInputNumberComponent,
  PasSpinnerComponent,
} from '@porsche-aftersales/frontend-toolbox';
import { isEmpty } from 'lodash-es';

import { SupplierService } from '../../../api/tur-wr/services';
import { TranslatePipe } from '../../../pipes/translate.pipe';
import { UserService } from '../../../services/user-service/user.service';
import { translate } from '../../../shared/fn/translate';

import { SupplierConditionStore } from './supplier-condition.store';

const ERRORS: (Omit<PasFormValidationMessage, 'message'> & { messageKey: string })[] = [
  {
    error: 'required',
    messageKey: 'validation.message.pattern',
  },
  {
    error: 'pattern',
    messageKey: 'validation.message.pattern',
  },
];

export const dateAsObject = (date: Date): { year: number; month: number; day: number } => {
  const year = date.getFullYear();
  const month = date.getMonth() + 1; // Months are 0-indexed, so we add 1
  const day = date.getDate();
  return { year, month, day };
};

@Component({
  selector: 'app-supplier-condition-editor',
  templateUrl: './supplier-condition-editor.component.html',
  styleUrls: ['./supplier-condition-editor.component.scss'],
  imports: [
    CommonModule,
    TranslatePipe,
    ReactiveFormsModule,
    PasInputFieldComponent,
    PasSpinnerComponent,
    PasInputNumberComponent,
    PasDateTimeComponent,
  ],
  providers: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SupplierConditionEditorComponent {
  private readonly supplierConditionStore = inject(SupplierConditionStore);
  private readonly supplierService = inject(SupplierService);
  private readonly translateService = inject(TranslateService);
  private readonly userService = inject(UserService);

  public readonly today = dateAsObject(new Date());
  public readonly longTimeAhead = dateAsObject(new Date(9999, 11, 31));

  public readonly validationMessages: PasFormValidationMessage[] = ERRORS.map(error => ({
    ...error,
    message: translate(error.messageKey),
  }));

  public readonly formGroup$ = this.supplierConditionStore.formGroup$;
  public spinnerActive = false;
  public decimalSeparator: ',' | '.' = '.';

  public constructor() {
    this.decimalSeparator = this.userService.userLanguage.startsWith('en') ? '.' : ',';
  }

  public onSupplierNumberChanged(form: FormGroup): void {
    const supplierNumberControl: any = form.get('supplierNumber');
    const supplierNumber: string = supplierNumberControl.value;

    if (isEmpty(supplierNumber) || supplierNumber.length < 6) {
      this.clearSupplierName(form);
    } else {
      this.spinnerActive = true;
      this.supplierService.findSupplierBySupplierNumber({ supplierNumber }).subscribe({
        next: supplierResponse => {
          if (supplierResponse?.supplierName?.trim().length > 0) {
            this.populateSupplierName(form, supplierResponse.supplierName.trim());
          } else {
            supplierNumberControl.setErrors({ incorrect: true, required: true });
            this.clearSupplierName(form);
          }
        },
        error: () => {
          supplierNumberControl.setErrors({ incorrect: true, required: true });
          this.clearSupplierName(form);
        },
        complete: () => {
          this.spinnerActive = false;
          // this trick forces 'supplierName' value to be rendered on the form,
          // by removing focus from 'supplierNumber' input
          // eslint-disable-next-line no-restricted-globals
          (window.document.activeElement as HTMLElement).blur();
        },
      });
    }
  }

  private clearSupplierName(form: FormGroup): void {
    form.get('supplierName').setValue(null);
    form.markAllAsTouched();
  }

  private populateSupplierName(form: FormGroup, supplierName: string): void {
    form.get('supplierName').setValue(supplierName);
    form.markAllAsTouched();
  }
}
